
import React, { useState } from 'react';
import { HomeIcon } from './icons/HomeIcon';
import { CheckEligibilityIcon } from './icons/CheckEligibilityIcon';
import { DashboardIcon } from './icons/DashboardIcon';
import { ResourcesIcon } from './icons/ResourcesIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { MenuIcon } from './icons/MenuIcon';
import { XIcon } from './icons/XIcon';
import { InformationCircleIcon } from './icons/InformationCircleIcon';
import TopBanner from './TopBanner';

interface HeaderProps {
    isLoggedIn: boolean;
    professionalEmail?: string;
    onNavigateHome: () => void;
    onNavigateToCheckEligibility: () => void;
    onNavigateToDashboard: () => void;
    onNavigateToResources: () => void;
    onNavigateToWhyForgivePath: () => void;
    onLoginClick: () => void;
    onCreateAccountClick: () => void;
    onLogoutClick: () => void;
}

const Logo: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button onClick={onClick} className="flex items-center gap-2 flex-shrink-0 cursor-pointer">
        <div className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center text-white">
            <SparkleIcon className="w-5 h-5" />
        </div>
        <span className="text-xl font-bold text-gray-800">ForgivePath</span>
    </button>
);

const NavLink: React.FC<{ 
    icon: React.ReactNode; 
    children: React.ReactNode; 
    onClick?: () => void;
    className?: string;
    isActive?: boolean;
}> = ({ icon, children, onClick, className = '', isActive = false }) => {
    const commonClasses = "flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium p-2 rounded-lg cursor-pointer";
    const activeClasses = isActive ? 'bg-gray-100 text-gray-900' : '';
    
    return (
        <li>
            <button onClick={onClick} className={`${commonClasses} ${activeClasses} ${className}`}>
                {icon}
                {children}
            </button>
        </li>
    );
};

const UserProfile: React.FC<{ email: string; onLogout: () => void; }> = ({ email, onLogout }) => (
    <div className="flex items-center gap-3">
        <div className="flex items-center gap-3 border-l pl-6">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {email.charAt(0).toUpperCase()}
            </div>
            <div>
                <p className="font-semibold text-sm text-gray-800">{email}</p>
                <p className="text-xs text-gray-500">Professional Account</p>
            </div>
            <button onClick={onLogout} title="Log Out" className="text-gray-400 hover:text-gray-700 cursor-pointer">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            </button>
        </div>
    </div>
);


const Header: React.FC<HeaderProps> = ({ isLoggedIn, professionalEmail, onNavigateHome, onNavigateToCheckEligibility, onNavigateToDashboard, onNavigateToResources, onNavigateToWhyForgivePath, onLoginClick, onCreateAccountClick, onLogoutClick }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleMobileLinkClick = (navigationFunction: () => void) => {
        navigationFunction();
        setIsMenuOpen(false);
    };
    
    return (
        <header className="fixed top-0 left-0 right-0 z-40 flex flex-col pointer-events-none h-auto">
            <div className="pointer-events-auto">
                <TopBanner />
            </div>
            <div className="border-b border-gray-200 bg-white/95 backdrop-blur-sm shadow-sm w-full pointer-events-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Logo onClick={onNavigateHome} />
                        
                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-4">
                            <ul className="flex items-center space-x-2">
                                <NavLink icon={<HomeIcon className="w-5 h-5" />} onClick={onNavigateHome}>Home</NavLink>
                                <NavLink icon={<CheckEligibilityIcon className="w-5 h-5" />} onClick={onNavigateToCheckEligibility}>Check Eligibility</NavLink>
                                 {isLoggedIn && <NavLink icon={<DashboardIcon className="w-5 h-5" />} onClick={onNavigateToDashboard} isActive={isLoggedIn}>My Dashboard</NavLink>}
                                <NavLink icon={<InformationCircleIcon className="w-5 h-5" />} onClick={onNavigateToWhyForgivePath}>Why Choose Us</NavLink>
                                <NavLink icon={<ResourcesIcon className="w-5 h-5" />} onClick={onNavigateToResources}>Resources</NavLink>
                            </ul>
                             {isLoggedIn ? (
                                <UserProfile email={professionalEmail!} onLogout={onLogoutClick} />
                             ) : (
                                <div className="flex items-center gap-2 border-l pl-6">
                                    <button 
                                        onClick={onLoginClick} 
                                        className="inline-flex items-center gap-2 font-black text-[11px] uppercase tracking-widest bg-gray-900 hover:bg-blue-600 text-white px-6 py-2.5 rounded-full transition-all shadow-md active:scale-95 cursor-pointer"
                                    >
                                        Sign In
                                        <SparkleIcon className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                             )}
                        </nav>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden">
                            <button onClick={() => setIsMenuOpen(true)} aria-label="Open menu" className="p-2 cursor-pointer pointer-events-auto">
                                <MenuIcon className="w-6 h-6 text-gray-700" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Panel */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden pointer-events-auto" role="dialog" aria-modal="true">
                    <div className="flex justify-between items-center h-16 px-4 sm:px-6 border-b">
                        <Logo onClick={() => handleMobileLinkClick(onNavigateHome)} />
                        <button onClick={() => setIsMenuOpen(false)} aria-label="Close menu" className="p-2 cursor-pointer">
                            <XIcon className="w-6 h-6 text-gray-700" />
                        </button>
                    </div>
                     {isLoggedIn ? (
                        <div className="p-4 border-b">
                            <UserProfile email={professionalEmail!} onLogout={() => handleMobileLinkClick(onLogoutClick)} />
                        </div>
                     ) : (
                        <div className="p-4 border-b">
                             <button 
                                onClick={() => handleMobileLinkClick(onLoginClick)} 
                                className="w-full flex items-center justify-center gap-2 font-black text-xs uppercase tracking-widest bg-gray-900 text-white px-4 py-4 rounded-xl shadow-lg cursor-pointer"
                            >
                                Sign In
                                <SparkleIcon className="w-4 h-4" />
                            </button>
                        </div>
                     )}
                    <nav className="flex-grow overflow-y-auto">
                        <ul className="flex flex-col p-4">
                            <NavLink className="w-full text-lg py-3" icon={<HomeIcon className="w-6 h-6" />} onClick={() => handleMobileLinkClick(onNavigateHome)}>Home</NavLink>
                            <NavLink className="w-full text-lg py-3" icon={<CheckEligibilityIcon className="w-6 h-6" />} onClick={() => handleMobileLinkClick(onNavigateToCheckEligibility)}>Check Eligibility</NavLink>
                             {isLoggedIn && <NavLink className="w-full text-lg py-3" icon={<DashboardIcon className="w-6 h-6" />} onClick={() => handleMobileLinkClick(onNavigateToDashboard)}>My Dashboard</NavLink>}
                            <NavLink className="w-full text-lg py-3" icon={<InformationCircleIcon className="w-6 h-6" />} onClick={() => handleMobileLinkClick(onNavigateToWhyForgivePath)}>Why Choose Us</NavLink>
                            <NavLink className="w-full text-lg py-3" icon={<ResourcesIcon className="w-6 h-6" />} onClick={() => handleMobileLinkClick(onNavigateToResources)}>Resources</NavLink>
                        </ul>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
